import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
  getConsumedMeta,
  isConsumedId,
} from '../../dux/modules/sharedEndpoints'
import * as appActions from '../../dux/actions/appActions'
import * as assetActions from '../../dux/actions/asset.js'
import * as endpointActions from '../../dux/actions/endpoint.js'
import * as folderActions from '../../dux/actions/folder.js'
import * as homeActions from '../../dux/actions/homeActions'
import * as modalActions from '../../dux/actions/modal'
import { UGC_FOLDER } from '../../dux/constants/folder'
import {
  getEndpointById,
  getFolderPendingCount,
  getRolledUpFolderPendingCount,
  getRootOfNode,
  getWidestPanel,
  isChild,
  validatePrivilege,
} from '../../dux/modules/utils'
import SidebarContent from './SidebarContent'
import {
  isSubmissionVisible,
  isMessageVisible,
} from '../notifications/notificationUtils'

import '../styles/Sidebar.css'

class Sidebar extends React.Component {
  componentDidMount = () => {
    window.setTimeout(this.setSidebarWidth, 50)
  }

  onCollapseChange = (keys) => {
    this.props.appActions.setOpenEndpointKeys(keys)
    window.setTimeout(this.setSidebarWidth, 50)
  }

  setSidebarWidth = () => {
    this.props.appActions.setMaxSidebarWidth(getWidestPanel())
  }

  /*
   * Only change active folder and scroll to top if
   * folder is changed.
   */
  onNodeClick = (id, endpoint, preserveStatusFilter = false) => {
    if (this.props.folders.activeFolder !== id) {
      this.props.folderActions.activeFolder(id)
    }

    if (!preserveStatusFilter && this.props.selectedFilter !== 'ALL') {
      this.props.appActions.applyStatusFilter('ALL')
    }

    if (endpoint && this.props.activeEndpoint !== endpoint) {
      this.props.appActions.setActiveEndpoint(endpoint)
    }

    if (
      this.props.menuItem !== 'library' &&
      this.props.menuItem !== 'builders'
    ) {
      this.props.appActions.setMenuItem('library')
    }
  }

  /*
   * Expand the panel if it's not expanded; Launch AddFolderModal passing
   *  appropriate type of folder to create
   */
  onPanelHeaderIconClick = (e, key) => {
    e.stopPropagation()
    const { activeFolder, byId } = this.props.folders

    if (this.props.openEndpointKeys.indexOf(key) < 0) {
      this.props.appActions.addOpenEndpointKey(key)
    }

    if (key === 'HOME')
      this.props.modalActions.openModal('ConfigUserEndpointModal')
    else {
      const endpoint = getEndpointById(key, this.props.endpoints.endpoints)

      const treeRoot = endpoint.rootFolders
      if (treeRoot.indexOf(getRootOfNode(byId[activeFolder], byId)) < 0) {
        this.props.folderActions.activeFolder(treeRoot[0])
      }

      this.props.modalActions.openModal('AddFolderModal', {
        treeRoot,
        onToggle: this.toggleFolder,
        onNodeClick: this.onNodeClick,
        endpointType: endpoint.type,
      })
    }
  }

  renamingCommitted = false

  handleRenameFolderKey = (e, id) => {
    if (e.key === 'Enter') {
      this.renamingCommitted = true
      this.props.folderActions.renameFolder(e.target.value, id)
      this.props.appActions.setRenamingFolder()
    }
  }

  handleRenameFolderBlur = (e, id) => {
    if (!this.renamingCommitted) {
      this.props.folderActions.renameFolder(e.target.value, id)
      this.props.appActions.setRenamingFolder()
    }
    this.renamingCommitted = false
  }

  toggleFolder = (id) => {
    if (this.props.openFolderKeys.indexOf(id) < 0) {
      this.props.appActions.addOpenFolderKey(id)
    } else {
      this.props.appActions.removeOpenFolderKey(id)
    }
    window.setTimeout(this.setSidebarWidth, 50)
  }

  nodeIsOpen = (id) => this.props.openFolderKeys.indexOf(id) > -1

  nodeIsActive = (id) => this.props.folders.activeFolder === id

  /*
   * A node is draggable if it is in the library, and not a root node
   */
  nodeIsDraggable = (id) => {
    /*
     * Consumed shared-endpoint content is read-only here. Its ROOT stays
     * draggable so a content admin can order it against the other roots —
     * that writes the consumer's own childFolders and persists. Everything
     * inside it belongs to the provider: dragging it produced an optimistic
     * reorder the API then rejected, which looked like it had worked.
     */
    if (isConsumedId(id)) {
      const meta = getConsumedMeta(this.props.folders.byId[id])
      return !!(meta && meta.root)
    }
    return true
  }

  nodeIsDisabled = (id) => {
    if (this.props.menuItem === 'builders') {
      if (
        id === '_search' ||
        id === this.props.folders.recycleBin ||
        isChild(id, this.props.folders.recycleBin, this.props.folders.byId)
      ) {
        return true
      }
      return !validatePrivilege(
        'createnews',
        this.props.permissions.privileges,
        this.props.user.email,
        this.props.permissions.groups,
        id,
        this.props.folders.byId,
      )
    }
    return false
  }

  nodeLink = (id) => `/library/${id}`

  setActiveFolder = (id) => {
    this.props.folderActions.activeFolder(id)
  }

  // Returns the number of pending items in the given folder; if the folder is open, only folder
  // contents are tested, If the folder is closed, contents of all subfolders are tested too.
  getBadgeCount = (id, type, rolledUp = true) => {
    const {
      endpoints: { folderAssetStatus },
      folders: { byId: allFolders },
      openFolderKeys,
    } = this.props

    switch (type) {
      case 'UGC':
        return id === UGC_FOLDER || allFolders[id].parentId === UGC_FOLDER
          ? allFolders[id].assets.length
          : 0
      case 'PENDING_APPROVAL':
      case 'PENDING_REMOVAL':
        if (!rolledUp || openFolderKeys.indexOf(id) > -1) {
          return getFolderPendingCount(id, folderAssetStatus, type, allFolders)
        }

        return getRolledUpFolderPendingCount(
          id,
          allFolders,
          folderAssetStatus,
          type,
        )

      default:
        return 0
    }
  }

  onBadgeClick = (e, id, badge) => {
    if (badge !== 'UGC') {
      e.preserveStatusFilter = false
      if (this.nodeIsOpen(id) && this.nodeIsActive(id)) e.stopPropagation()

      if (this.nodeIsOpen(id)) e.preserveStatusFilter = true

      const folderPendingItems = this.getBadgeCount(id, badge, false)
      if (folderPendingItems) {
        e.preserveStatusFilter = true
        if (this.props.selectedFilter !== badge)
          this.props.appActions.applyStatusFilter(badge)
      }
    }
  }

  // Returns true if the endpoint allows publishing.  Used by the nodes when deciding whether
  // to display a badge of pending items
  allowPublishing = (endpointId) => {
    const endpoint = getEndpointById(endpointId, this.props.endpoints.endpoints)
    return endpoint.allowPublishing
  }

  // Derive the list of filter options for the inbox nav children.
  // Returns an array of { key, label } objects — one per distinct message category
  // and one per distinct form. Only called when notificationsEnabled is true.
  getNotificationsFilterOptions = () => {
    var options = []
    var { notifications, formSubmissions, user, inboxConfig } = this.props
    var userEmail = user && user.email

    // Collect distinct message categories
    if (notifications && notifications.messages) {
      var seenCategories = {}
      notifications.messages.forEach(function (msg) {
        if (msg.deleted) return
        if (!isMessageVisible(msg, inboxConfig)) return
        var cat = msg.category || '_messages'
        if (!seenCategories[cat]) {
          seenCategories[cat] = true
          options.push({
            key: 'message:' + cat,
            label: cat === '_messages' ? 'Messages' : cat,
          })
        }
      })
    }

    // Collect distinct form IDs
    if (formSubmissions && formSubmissions.submissions) {
      var seenForms = {}
      formSubmissions.submissions.forEach(function (sub) {
        if (!isSubmissionVisible(sub, inboxConfig)) return
        if (!seenForms[sub.formId]) {
          seenForms[sub.formId] = true
          options.push({
            key: 'submission:' + sub.formId,
            label: sub.formTitle || sub.formId,
          })
        }
      })
    }

    return options
  }

  // Get total unread notifications count (messages + form submissions),
  // limited to items that are visible per the inbox config.
  getTotalUnreadCount = () => {
    const { notifications, formSubmissions, user, inboxConfig } = this.props

    // Count unread visible messages
    const unreadMessages =
      notifications && notifications.messages
        ? notifications.messages.filter(function (msg) {
            return (
              !msg.deleted && !msg.viewed && isMessageVisible(msg, inboxConfig)
            )
          }).length
        : 0

    // Count unread visible form submissions
    const userEmail = user && user.email
    const unreadSubmissions =
      formSubmissions && formSubmissions.submissions
        ? formSubmissions.submissions.filter(function (sub) {
            return (
              (!sub.recipientViewed || !sub.recipientViewed[userEmail]) &&
              isSubmissionVisible(sub, inboxConfig)
            )
          }).length
        : 0

    return unreadMessages + unreadSubmissions
  }

  render() {
    return (
      <SidebarContent
        activeKey={this.props.activeEndpoint}
        activeFolder={this.props.folders.activeFolder}
        onCollapseChange={this.onCollapseChange}
        onPanelHeaderIconClick={this.onPanelHeaderIconClick}
        folders={this.props.folders}
        handleRenameFolder={this.handleRenameFolderKey}
        handleRenameFolderBlur={this.handleRenameFolderBlur}
        toggleFolder={this.toggleFolder}
        onNodeClick={this.onNodeClick}
        nodeLink={this.nodeLink}
        nodeIsOpen={this.nodeIsOpen}
        nodeIsActive={this.nodeIsActive}
        nodeIsDraggable={this.nodeIsDraggable}
        nodeIsDisabled={this.nodeIsDisabled}
        endpoints={this.props.endpoints}
        openFolderKeys={this.props.openFolderKeys}
        removeOpenFolderKey={this.props.appActions.removeOpenFolderKey}
        openEndpointKeys={this.props.openEndpointKeys}
        renamingFolder={this.props.renamingFolder}
        folderActions={this.props.folderActions}
        modalActions={this.props.modalActions}
        appActions={this.props.appActions}
        assetActions={this.props.assetActions}
        endpointActions={this.props.endpointActions}
        email={this.props.user.email}
        homeActions={this.props.homeActions}
        groups={this.props.permissions.groups}
        privileges={this.props.permissions.privileges}
        history={this.props.history}
        menuItem={this.props.menuItem}
        setActiveFolder={this.setActiveFolder}
        getBadgeCount={this.getBadgeCount}
        onBadgeClick={this.onBadgeClick}
        allowPublishing={this.allowPublishing}
        relationships={this.props.relationships}
        activeView={this.props.activeView}
        allAssets={this.props.assets.byId}
        selectedAssets={this.props.selectedAssets}
        folderDragData={this.props.folderDragData}
        notificationsBadgeCount={this.getTotalUnreadCount()}
        notificationsEnabled={this.props.notificationsEnabled}
        notificationsFilter={this.props.notificationsFilter}
        notificationsFilterOptions={this.getNotificationsFilterOptions()}
        onSetNotificationsFilter={this.props.appActions.setNotificationsFilter}
        libraryLabel={this.props.libraryLabel}
      />
    )
  }
}

Sidebar.propTypes = {
  user: PropTypes.object,
  folders: PropTypes.object,
  activeFolder: PropTypes.string,
  moveFunction: PropTypes.func,
  onGetFolderClick: PropTypes.func,
  onActiveFolderClick: PropTypes.func,
  relationships: PropTypes.object,
  activeEndpoint: PropTypes.string,
  menuItem: PropTypes.string,
  activeView: PropTypes.string,
  openFolderKeys: PropTypes.arrayOf(PropTypes.string),
  openEndpointKeys: PropTypes.arrayOf(PropTypes.string),
  renamingFolder: PropTypes.string,
  ContextMenuTrigger: PropTypes.string,
  folderDragData: PropTypes.object,
}

const mapStateToProps = (state) => ({
  folders: state.folders,
  assets: state.assets,
  user: state.user,
  selectedAssets: state.appState.selectedAssets,
  activeEndpoint: state.appState.activeEndpoint,
  openFolderKeys: state.appState.openFolderKeys,
  openEndpointKeys: state.appState.openEndpointKeys,
  renamingFolder: state.appState.renamingFolder,
  selectedFilter: state.appState.selectedFilter,
  menuItem: state.appState.menuItem,
  activeView: state.appState.activeView,
  endpoints: state.endpoints,
  permissions: state.permissions,
  relationships: state.assets.relationships,
  folderDragData: state.appState.folderDragData,
  notifications: state.notifications,
  formSubmissions: state.formSubmissions,
  notificationsEnabled: !!(
    state.settings.modules && state.settings.modules.notifications
  ),
  inboxConfig:
    state.settings.modules && state.settings.modules.notifications
      ? state.settings.modules.notifications.inbox
      : null,
  notificationsFilter: state.appState.notificationsFilter,
  libraryLabel:
    (state.settings.menu &&
      state.settings.menu.modules &&
      state.settings.menu.modules.library) ||
    'Library',
})

const mapDispatchToProps = (dispatch) => ({
  folderActions: bindActionCreators(folderActions, dispatch),
  modalActions: bindActionCreators(modalActions, dispatch),
  assetActions: bindActionCreators(assetActions, dispatch),
  endpointActions: bindActionCreators(endpointActions, dispatch),
  appActions: bindActionCreators(appActions, dispatch),
  homeActions: bindActionCreators(homeActions, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar)
